<template>
  <div class="offsite-gallery-page">
    <div class="gallery-nav">
      <router-link to="/offsite" class="back-link">← Back To OFFSITE</router-link>
    </div>

    <div class="gallery-grid">
      <div
        v-for="(img, i) in images"
        :key="i"
        class="gallery-item"
        @click="openLightbox(i)"
      >
        <img :src="img.thumb" :alt="`OFFSITE 2025 photo ${i + 1}`" loading="lazy" />
      </div>
    </div>

    <teleport to="body">
      <div v-if="lightboxOpen" class="offsite-lightbox" @click.self="closeLightbox">
        <button class="lb-close" @click="closeLightbox" aria-label="Close">×</button>
        <button class="lb-prev" @click="prev" aria-label="Previous">‹</button>
        <img :src="images[currentIndex].full" :alt="`OFFSITE 2025 photo ${currentIndex + 1}`" />
        <button class="lb-next" @click="next" aria-label="Next">›</button>
        <div class="lb-counter">{{ currentIndex + 1 }} / {{ images.length }}</div>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'

const images = ref([])
const lightboxOpen = ref(false)
const currentIndex = ref(0)

async function fetchImages() {
  const base = '/wp-json/wp/v2/media?search=ORE-Offsite-NOLA-2025&per_page=100'
  const first = await fetch(`${base}&page=1`)
  const totalPages = parseInt(first.headers.get('X-WP-TotalPages') || '1')
  const pages = [first.json()]
  for (let p = 2; p <= totalPages; p++) {
    pages.push(fetch(`${base}&page=${p}`).then(r => r.json()))
  }
  const results = await Promise.all(pages)
  const all = results.flat()
  all.sort((a, b) => {
    const na = parseInt(a.slug.match(/(\d+)$/)?.[1] || 0)
    const nb = parseInt(b.slug.match(/(\d+)$/)?.[1] || 0)
    return na - nb
  })
  images.value = all.map(m => ({
    thumb: m.media_details?.sizes?.large?.source_url || m.source_url,
    full: m.source_url,
  }))
}

function openLightbox(i) {
  currentIndex.value = i
  lightboxOpen.value = true
  document.body.style.overflow = 'hidden'
}

function closeLightbox() {
  lightboxOpen.value = false
  document.body.style.overflow = ''
}

function prev() {
  currentIndex.value = (currentIndex.value - 1 + images.value.length) % images.value.length
}

function next() {
  currentIndex.value = (currentIndex.value + 1) % images.value.length
}

function onKey(e) {
  if (!lightboxOpen.value) return
  if (e.key === 'Escape') closeLightbox()
  if (e.key === 'ArrowLeft') prev()
  if (e.key === 'ArrowRight') next()
}

onMounted(() => {
  window.addEventListener('keydown', onKey)
  fetchImages()
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey)
  document.body.style.overflow = ''
})
</script>

<style lang="scss" scoped>
$black: #000;
$white: #fff;
$orange: #e8521a;

.offsite-gallery-page {
  background: $black;
  min-height: 100vh;
  padding-top: 80px;
}

.gallery-nav {
  padding: 24px 32px;
  display: flex;
  justify-content: center;

  .back-link {
    display: inline-block;
    background: $orange;
    color: $white;
    text-decoration: none;
    padding: 10px 24px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.03em;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

.gallery-grid {
  columns: 4;
  column-gap: 0;

  @media (max-width: 1024px) { columns: 3; }
  @media (max-width: 640px)  { columns: 2; }

  .gallery-item {
    break-inside: avoid;
    cursor: pointer;
    overflow: hidden;
    display: block;

    img {
      display: block;
      width: 100%;
      height: auto;
      transition: transform 0.3s ease;
    }

    &:hover img {
      transform: scale(1.03);
    }
  }
}
</style>

<style lang="scss">
.page-gallery .main-header:not(.is-scrolled) {
  .main-header__nav-list a {
    color: #fff !important;
  }
  .main-header__logo svg path,
  .main-header__logo svg rect,
  .main-header__logo svg polygon {
    fill: #fff !important;
  }
}

.page-gallery .main-footer {
  display: none;
}

.offsite-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-width: 90vw;
    max-height: 88vh;
    object-fit: contain;
    display: block;
    user-select: none;
  }

  .lb-close {
    position: absolute;
    top: 20px;
    right: 28px;
    background: none;
    border: none;
    color: #fff;
    font-size: 40px;
    line-height: 1;
    cursor: pointer;
    opacity: 0.8;
    padding: 4px 8px;
    &:hover { opacity: 1; }
  }

  .lb-prev,
  .lb-next {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #fff;
    font-size: 56px;
    line-height: 1;
    cursor: pointer;
    opacity: 0.7;
    padding: 8px 16px;
    &:hover { opacity: 1; }
  }

  .lb-prev { left: 16px; }
  .lb-next { right: 16px; }

  .lb-counter {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    color: rgba(255, 255, 255, 0.6);
    font-size: 13px;
    letter-spacing: 0.05em;
  }
}
</style>
